import { ZIndexClass } from "@/constants/z-index";
import { pathRequiresAuthenticationAndTeamMembership } from "@/utils/networking-constants";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

// Set to true to preview the loading treatment without navigating.
const DEBUG_ALWAYS_VISIBLE = false;

export const RouteLoadingIndicator: React.FC = () => {
  const { events } = useRouter();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let activeUrl: string | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancellationTimer: ReturnType<typeof setTimeout> | undefined;
    let shown = false;

    const reset = () => {
      clearTimeout(timer);
      clearTimeout(cancellationTimer);
      activeUrl = null;
      shown = false;
      setVisible(false);
    };

    const start = (url: string, { shallow }: { shallow: boolean }) => {
      // Shallow filters and tabs reuse the loaded page and need no route-loading treatment.
      if (
        shallow ||
        !pathRequiresAuthenticationAndTeamMembership(new URL(url, window.location.origin).pathname)
      ) {
        reset();
        return;
      }
      clearTimeout(cancellationTimer);
      activeUrl = url;
      if (!shown) {
        clearTimeout(timer);
        timer = setTimeout(() => {
          shown = true;
          setVisible(true);
        }, 150);
      }
    };

    const finish = (url: string) => {
      // A cancelled navigation can finish after its replacement has started.
      if (url === activeUrl) reset();
    };
    const fail = (error: { cancelled?: boolean }, url: string) => {
      if (url !== activeUrl) return;
      if (!error.cancelled) {
        finish(url);
        return;
      }
      // Next cancels before starting a replacement; a short grace period bridges those events.
      clearTimeout(timer);
      cancellationTimer = setTimeout(() => finish(url), 150);
    };

    events.on("routeChangeStart", start);
    events.on("routeChangeComplete", finish);
    events.on("routeChangeError", fail);
    events.on("hashChangeStart", reset);
    return () => {
      clearTimeout(timer);
      clearTimeout(cancellationTimer);
      events.off("routeChangeStart", start);
      events.off("routeChangeComplete", finish);
      events.off("routeChangeError", fail);
      events.off("hashChangeStart", reset);
    };
  }, [events]);

  return visible || DEBUG_ALWAYS_VISIBLE ? (
    <div
      role="progressbar"
      aria-label="Loading page"
      className={`route-loading-indicator pointer-events-none fixed inset-x-0 top-0 h-[2px] overflow-hidden ${ZIndexClass.COMMAND_PALETTE}`}
    >
      <div className="route-loading-indicator-bar h-full w-1/3 bg-cds-bright-green-500 dark:bg-cds-bright-green-400" />
    </div>
  ) : null;
};
